import React, { useRef, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Upload,
  FileSpreadsheet,
  FileArchive,
  Download,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Sparkles,
  RefreshCcw,
  X,
} from "lucide-react";

const Empty = ({ icon: Icon, label, hint }) => (
  <div className="flex flex-col items-center justify-center text-center py-8">
    <Icon className="w-12 h-12 text-zinc-700 mb-3" strokeWidth={1.5} />
    <div className="text-zinc-400 font-heading uppercase text-sm tracking-widest">{label}</div>
    <div className="text-zinc-600 text-xs mt-2 max-w-xs">{hint}</div>
  </div>
);

const FilePicker = ({ label, hint, accept, file, onChange, testId, icon: Icon }) => {
  const ref = useRef();
  return (
    <div
      className="border-2 border-dashed border-zinc-800 bg-zinc-950 p-6 text-center hover:border-yellow-400 transition cursor-pointer"
      onClick={() => ref.current?.click()}
      data-testid={testId}
    >
      <input
        ref={ref}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => onChange(e.target.files?.[0] || null)}
      />
      {file ? (
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 text-left min-w-0">
            <Icon className="w-8 h-8 text-yellow-400 flex-shrink-0" strokeWidth={2} />
            <div className="min-w-0">
              <div className="text-white font-bold truncate">{file.name}</div>
              <div className="text-zinc-500 text-xs">{(file.size / 1024).toFixed(1)} KB</div>
            </div>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onChange(null);
            }}
            className="text-zinc-500 hover:text-red-400 flex-shrink-0"
            aria-label="Rimuovi file"
          >
            <X className="w-5 h-5" strokeWidth={3} />
          </button>
        </div>
      ) : (
        <>
          <Icon className="w-10 h-10 text-zinc-600 mx-auto mb-3" strokeWidth={1.5} />
          <div className="font-heading uppercase text-white text-sm tracking-widest">{label}</div>
          <div className="text-zinc-500 text-xs mt-2">{hint}</div>
        </>
      )}
    </div>
  );
};

const StatBox = ({ label, value, tone = "default" }) => {
  const tones = {
    default: "bg-zinc-900 border-zinc-800 text-white",
    success: "bg-green-500/10 border-green-500/30 text-green-400",
    warning: "bg-yellow-400/10 border-yellow-400/40 text-yellow-400",
    error: "bg-red-500/10 border-red-500/30 text-red-400",
  };
  return (
    <div className={`border-2 p-4 ${tones[tone]}`}>
      <div className="text-3xl font-heading">{value}</div>
      <div className="text-xs uppercase tracking-widest mt-1 opacity-70">{label}</div>
    </div>
  );
};

const RowBadge = ({ row }) => {
  if (row.errors?.length) {
    return (
      <span
        className="inline-flex items-center gap-1 bg-red-500/20 text-red-400 px-2 py-0.5 text-xs uppercase font-bold border border-red-500/40"
        data-testid={`row-badge-error-${row.row_number}`}
      >
        <AlertCircle className="w-3 h-3" /> Errore
      </span>
    );
  }
  if (row.is_update) {
    return (
      <span
        className="inline-flex items-center gap-1 bg-blue-500/20 text-blue-400 px-2 py-0.5 text-xs uppercase font-bold border border-blue-500/40"
        data-testid={`row-badge-update-${row.row_number}`}
      >
        <RefreshCcw className="w-3 h-3" /> Aggiorna
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 bg-green-500/20 text-green-400 px-2 py-0.5 text-xs uppercase font-bold border border-green-500/40"
      data-testid={`row-badge-new-${row.row_number}`}
    >
      <Sparkles className="w-3 h-3" /> Nuovo
    </span>
  );
};

export default function ImportProductsPage() {
  const [excelFile, setExcelFile] = useState(null);
  const [zipFile, setZipFile] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [report, setReport] = useState(null);
  const [processBg, setProcessBg] = useState(true);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/admin/products/bulk-import/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "template_prodotti_daitanjack.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Template scaricato");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore download");
    }
  };

  const runPreview = async () => {
    if (!excelFile) {
      toast.error("Carica prima il file Excel");
      return;
    }
    setPreviewing(true);
    setReport(null);
    try {
      const fd = new FormData();
      fd.append("file", excelFile);
      if (zipFile) fd.append("images_zip", zipFile);
      const { data } = await api.post("/admin/products/bulk-import/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      if (data.global_errors?.length) {
        toast.error(data.global_errors.join(" · "));
      } else {
        toast.success(`Anteprima OK: ${data.stats.valid}/${data.stats.total} righe valide`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore anteprima");
    } finally {
      setPreviewing(false);
    }
  };

  const runImport = async () => {
    if (!preview || !preview.ok) return;
    if (preview.stats.valid === 0) {
      toast.error("Nessuna riga valida da importare");
      return;
    }
    const msg =
      `Confermi l'import?\n\n` +
      `• ${preview.stats.creates} nuovi prodotti\n` +
      `• ${preview.stats.updates} prodotti aggiornati\n` +
      `• ${preview.stats.with_errors} righe con errori (verranno saltate)\n\n` +
      `L'operazione può richiedere ~${Math.max(15, preview.stats.valid * 8)} secondi per il processing AI delle immagini.`;
    if (!window.confirm(msg)) return;

    setImporting(true);
    setReport(null);
    try {
      const fd = new FormData();
      fd.append("file", excelFile);
      if (zipFile) fd.append("images_zip", zipFile);
      fd.append("skip_errors", "true");
      fd.append("process_bg", processBg ? "true" : "false");
      const { data } = await api.post("/admin/products/bulk-import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000, // 5 min
      });
      setReport(data);
      toast.success(`Import completato: ${data.created} creati, ${data.updated} aggiornati`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore import");
    } finally {
      setImporting(false);
    }
  };

  const reset = () => {
    setExcelFile(null);
    setZipFile(null);
    setPreview(null);
    setReport(null);
  };

  return (
    <div data-testid="admin-import-products" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="font-jp text-yellow-400 text-xs tracking-widest mb-2">一括インポート</div>
          <h1 className="font-heading text-5xl uppercase text-white leading-none">Importa Prodotti</h1>
          <p className="text-zinc-500 mt-3 max-w-2xl text-sm">
            Carica un file Excel compilato con il template e (opzionalmente) uno ZIP con le immagini
            (naming: <span className="text-yellow-400 font-mono">{"{SKU}_{N}.jpg"}</span>).
            <br />
            <span className="text-zinc-400">
              <strong className="text-yellow-400">Nessun campo è obbligatorio</strong>: se manca lo SKU
              ne genero uno automatico (la riga verrà creata come <strong>nuovo</strong> prodotto);
              per titolo, prezzo e categoria assegno valori di default (segnalati come warning).
              Puoi anche usare una colonna <span className="text-yellow-400 font-mono">URL Immagine</span>
              con link diretti (separati da <code>|</code> per più immagini) al posto dello ZIP.
            </span>
          </p>
        </div>
        <button
          type="button"
          onClick={downloadTemplate}
          data-testid="import-download-template"
          className="text-zinc-300 hover:text-yellow-400 text-xs uppercase tracking-widest flex items-center gap-2 border border-zinc-800 hover:border-yellow-400 px-3 py-2 transition"
        >
          <Download className="w-4 h-4" strokeWidth={3} /> Scarica template
        </button>
      </div>

      {/* Upload Zone */}
      <div className="bg-zinc-900 border-2 border-zinc-800 p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FilePicker
            label="File Excel (obbligatorio)"
            hint=".xlsx compilato con il template Daita 'n' Jack"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            file={excelFile}
            onChange={setExcelFile}
            testId="import-excel-picker"
            icon={FileSpreadsheet}
          />
          <FilePicker
            label="ZIP Immagini (opzionale)"
            hint="Foto in JPG/PNG/WEBP con nome {SKU}_{N}.jpg"
            accept=".zip,application/zip"
            file={zipFile}
            onChange={setZipFile}
            testId="import-zip-picker"
            icon={FileArchive}
          />
        </div>

        {/* Toggle: applica sfondo nero AI a tutte le immagini dello ZIP */}
        <button
          type="button"
          role="switch"
          aria-checked={processBg}
          data-testid="toggle-process-bg-bulk"
          onClick={() => setProcessBg((v) => !v)}
          disabled={importing}
          className={`w-full flex items-center justify-between gap-3 border-2 px-4 py-3 transition ${
            processBg
              ? "bg-yellow-400/10 border-yellow-400 text-yellow-400"
              : "bg-zinc-900 border-zinc-800 text-zinc-400 hover:border-zinc-600"
          } disabled:opacity-50`}
        >
          <div className="flex items-center gap-3 min-w-0">
            <Sparkles className="w-5 h-5 flex-shrink-0" strokeWidth={3} />
            <div className="text-left min-w-0">
              <div className="font-heading uppercase text-sm tracking-widest">
                Sfondo nero automatico (AI) su tutte le immagini
              </div>
              <div className="text-xs opacity-70 mt-0.5">
                {processBg
                  ? "Attivo: rimuove sfondo di ogni foto + applica nero (consigliato per foto naturali)"
                  : "Disattivo: mantiene le immagini originali dello ZIP (usa per render 3D o foto già pulite)"}
              </div>
            </div>
          </div>
          <div
            className={`w-12 h-6 flex-shrink-0 border-2 relative transition ${
              processBg ? "bg-yellow-400 border-yellow-400" : "bg-zinc-800 border-zinc-700"
            }`}
          >
            <div
              className={`absolute top-0.5 w-4 h-4 bg-black transition-all ${
                processBg ? "left-[calc(100%-18px)]" : "left-0.5"
              }`}
            />
          </div>
        </button>

        <div className="flex gap-3 flex-wrap">
          <button
            type="button"
            onClick={runPreview}
            disabled={!excelFile || previewing || importing}
            data-testid="import-preview-btn"
            className="bg-white text-black font-heading uppercase px-6 py-3 hover:bg-yellow-400 transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {previewing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" strokeWidth={3} />}
            {previewing ? "Analisi in corso…" : "Anteprima"}
          </button>
          {preview?.ok && (
            <button
              type="button"
              onClick={runImport}
              disabled={importing || preview.stats.valid === 0}
              data-testid="import-confirm-btn"
              className="bg-yellow-400 text-black font-heading uppercase px-6 py-3 hover:bg-white transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {importing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" strokeWidth={3} />}
              {importing ? "Import in corso…" : `Conferma import (${preview.stats.valid})`}
            </button>
          )}
          {(excelFile || zipFile || preview || report) && (
            <button
              type="button"
              onClick={reset}
              disabled={previewing || importing}
              data-testid="import-reset-btn"
              className="text-zinc-400 hover:text-red-400 font-heading uppercase text-sm tracking-widest px-4 py-3 border border-zinc-800 hover:border-red-500 transition"
            >
              Ripristina
            </button>
          )}
        </div>
      </div>

      {/* Global errors */}
      {preview?.global_errors?.length > 0 && (
        <div className="bg-red-500/10 border-2 border-red-500/40 p-4 space-y-2">
          {preview.global_errors.map((err, i) => (
            <div key={i} className="flex items-start gap-2 text-red-400 text-sm">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" strokeWidth={3} />
              <span>{err}</span>
            </div>
          ))}
        </div>
      )}

      {/* Preview stats */}
      {preview?.ok && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="import-preview-stats">
          <StatBox label="Righe totali" value={preview.stats.total} />
          <StatBox label="Valide" value={preview.stats.valid} tone="success" />
          <StatBox label="Errori" value={preview.stats.with_errors} tone="error" />
          <StatBox label="Nuovi" value={preview.stats.creates} tone="success" />
          <StatBox label="Aggiornati" value={preview.stats.updates} tone="warning" />
        </div>
      )}

      {/* Preview table */}
      {preview?.rows?.length > 0 && !report && (
        <div className="bg-zinc-900 border-2 border-zinc-800 overflow-x-auto" data-testid="import-preview-table">
          <table className="w-full">
            <thead className="bg-zinc-950 border-b-2 border-zinc-800">
              <tr className="text-left">
                {["#", "Stato", "SKU", "Titolo", "Categoria", "Prezzo", "Stock", "Immagini", "Note"].map((h) => (
                  <th key={h} className="font-heading uppercase text-yellow-400 text-xs tracking-widest p-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {preview.rows.map((r) => (
                <tr
                  key={r.row_number}
                  data-testid={`preview-row-${r.row_number}`}
                  className={`border-b border-zinc-800 ${r.errors?.length ? "bg-red-500/5" : ""}`}
                >
                  <td className="p-3 text-zinc-500 text-xs">{r.row_number}</td>
                  <td className="p-3"><RowBadge row={r} /></td>
                  <td className="p-3 text-white text-sm font-mono">{r.sku || "—"}</td>
                  <td className="p-3 text-white text-sm max-w-xs truncate" title={r.title || ""}>{r.title || "—"}</td>
                  <td className="p-3 text-zinc-400 text-sm">{r.category || "—"}</td>
                  <td className="p-3 text-yellow-400 font-bold text-sm">{r.price != null ? `€ ${r.price.toFixed(2)}` : "—"}</td>
                  <td className="p-3 text-zinc-300 text-sm">{r.stock ?? 0}</td>
                  <td className="p-3 text-zinc-400 text-sm">
                    {r.images_matched?.length > 0 ? (
                      <span className="text-yellow-400">{r.images_matched.length}</span>
                    ) : (
                      <span className="text-zinc-600">0</span>
                    )}
                  </td>
                  <td className="p-3 text-xs">
                    {r.errors?.length > 0 && (
                      <div className="text-red-400 space-y-0.5">
                        {r.errors.map((e, i) => <div key={i}>• {e}</div>)}
                      </div>
                    )}
                    {r.warnings?.length > 0 && (
                      <div className="text-yellow-500/80 space-y-0.5">
                        {r.warnings.map((w, i) => <div key={i}>! {w}</div>)}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Unmatched images */}
      {preview?.unmatched_images?.length > 0 && !report && (
        <div className="bg-yellow-400/10 border-2 border-yellow-400/40 p-4">
          <div className="flex items-center gap-2 text-yellow-400 font-heading uppercase text-sm tracking-widest mb-2">
            <AlertCircle className="w-4 h-4" strokeWidth={3} />
            {preview.unmatched_images.length} immagini nello ZIP senza SKU corrispondente
          </div>
          <div className="text-zinc-400 text-xs font-mono flex flex-wrap gap-1">
            {preview.unmatched_images.slice(0, 30).map((f, i) => (
              <span key={i} className="bg-zinc-950 px-2 py-0.5">{f}</span>
            ))}
            {preview.unmatched_images.length > 30 && (
              <span className="text-zinc-500">…e altre {preview.unmatched_images.length - 30}</span>
            )}
          </div>
        </div>
      )}

      {/* Import Report */}
      {report && (
        <div className="space-y-4" data-testid="import-report">
          <div className="bg-green-500/10 border-2 border-green-500/40 p-6">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-400" strokeWidth={2.5} />
              <h3 className="font-heading uppercase text-2xl text-green-400">Import completato</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatBox label="Creati" value={report.created} tone="success" />
              <StatBox label="Aggiornati" value={report.updated} tone="warning" />
              <StatBox label="Saltati" value={report.skipped} tone="error" />
              <StatBox label="Totale righe" value={report.total_rows} />
            </div>
          </div>

          <div className="bg-zinc-900 border-2 border-zinc-800 overflow-x-auto">
            <table className="w-full">
              <thead className="bg-zinc-950 border-b-2 border-zinc-800">
                <tr className="text-left">
                  {["#", "Azione", "SKU", "Titolo", "Immagini", "Note"].map((h) => (
                    <th key={h} className="font-heading uppercase text-yellow-400 text-xs tracking-widest p-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {report.rows.map((r) => (
                  <tr key={r.row_number} className="border-b border-zinc-800">
                    <td className="p-3 text-zinc-500 text-xs">{r.row_number}</td>
                    <td className="p-3">
                      <span className={`inline-block px-2 py-0.5 text-xs uppercase font-bold border ${
                        r.action === "created" ? "bg-green-500/20 text-green-400 border-green-500/40" :
                        r.action === "updated" ? "bg-blue-500/20 text-blue-400 border-blue-500/40" :
                        "bg-red-500/20 text-red-400 border-red-500/40"
                      }`}>
                        {r.action}
                      </span>
                    </td>
                    <td className="p-3 text-white text-sm font-mono">{r.sku || "—"}</td>
                    <td className="p-3 text-white text-sm max-w-xs truncate" title={r.title || ""}>{r.title || "—"}</td>
                    <td className="p-3 text-yellow-400 text-sm">{r.images_processed}</td>
                    <td className="p-3 text-xs">
                      {r.errors?.length > 0 && (
                        <div className="text-red-400">{r.errors.join(" · ")}</div>
                      )}
                      {r.warnings?.length > 0 && (
                        <div className="text-yellow-500/80">{r.warnings.join(" · ")}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!excelFile && !preview && !report && (
        <div className="bg-zinc-900/30 border-2 border-dashed border-zinc-800 p-8">
          <Empty
            icon={Upload}
            label="Carica un file Excel per iniziare"
            hint="Scarica prima il template ufficiale se non ce l'hai. Il file ZIP con le immagini è opzionale ma consigliato."
          />
        </div>
      )}
    </div>
  );
}
